import React from "react";

export type ApiRecord = Record<string, any>;

export const UNKNOWN = "Unknown / not supplied";

export const CANONICAL_STAGES: ReadonlyArray<readonly [string, string]> = [
  ["NOT_REVIEWED", "Needs review"],
  ["GC_APPROVED_FOR_CONSTRUCTPAY_OUTREACH", "GC-authorized outreach"],
  ["CONSTRUCTPAY_OUTREACH_STARTED", "ConstructPay outreach"],
  ["TRADE_PARTNER_REVIEWING", "Trade partner review"],
  ["TRADE_PARTNER_APPROVED_INTRODUCTION", "Trade partner approval"],
  ["INTRODUCED_TO_EXTERNAL_PARTNER", "Bounded partner introduction"],
  ["PARTNER_REVIEWING", "Partner review"],
  ["DOCS_REQUESTED", "Documents requested"],
  ["TERMS_BOND_PATH_RECEIVED", "Terms or bond path received"],
  ["CLOSED", "Closed"],
];

export function workflowLabel(status: any): string {
  if (status === "DECLINED") return "Declined";
  return CANONICAL_STAGES.find(([candidate]) => candidate === status)?.[1] || UNKNOWN;
}

export function qualityLabel(status: any): string {
  const labels: Record<string, string> = {
    CURRENT: "Structured snapshot current",
    STALE: "Source data is stale",
    CONFLICT: "Source conflict",
    SOURCE_PENDING: "Source status is unknown / pending",
  };
  return labels[status] || UNKNOWN;
}

export function opportunityTypeLabel(opportunityType: any): string {
  if (opportunityType === "BOTH") return "Bonding + Working Capital";
  if (opportunityType === "BOND") return "Bonding";
  if (opportunityType === "FUNDING") return "Working Capital";
  return UNKNOWN;
}

/**
 * Broker-facing relationship label from actual request state.
 * Does not invent amounts. Zero formal requests → relationship/readiness wording,
 * not "Bonding + Working Capital".
 */
export function opportunityRelationshipLabel(opportunity: {
  opportunityType?: any;
  requestCount?: any;
  bondAmount?: any;
  fundingAmount?: any;
  requests?: Array<{ requestType?: any }> | null;
}): string {
  const requests = Array.isArray(opportunity.requests) ? opportunity.requests : null;
  const hasBond = requests
    ? requests.some((item) => item.requestType === "BOND")
    : opportunity.bondAmount !== null && opportunity.bondAmount !== undefined;
  const hasFunding = requests
    ? requests.some((item) => item.requestType === "FUNDING")
    : opportunity.fundingAmount !== null && opportunity.fundingAmount !== undefined;
  const count = Number(opportunity.requestCount);
  const zeroRequests =
    (requests ? requests.length === 0 : false)
    || (Number.isFinite(count) && count <= 0)
    || (!hasBond && !hasFunding && (!Number.isFinite(count) || count <= 0));

  if (hasBond && hasFunding) return "Bonding + Working Capital";
  if (hasBond) return "Bonding";
  if (hasFunding) return "Working Capital";
  if (zeroRequests) return "Bonding Relationship";
  return opportunityTypeLabel(opportunity.opportunityType);
}

export function originLabel(originType: any): string {
  if (originType === "DIRECT_CUSTOMER") return "Direct customer opportunity";
  if (originType === "GC_SPONSORED") return "GC-sponsored opportunity";
  return UNKNOWN;
}

export function gate2BrokerLabel(state: any): { label: string; tone: "lock" | "amber" | "green" | "red" } {
  if (state === "AUTHORIZED") return { label: "External sharing authorized", tone: "green" };
  if (state === "DENIED" || state === "REVOKED") return { label: "External sharing declined", tone: "red" };
  if (state === "PENDING") return { label: "External sharing not yet authorized", tone: "amber" };
  if (state === "INITIAL" || !state) return { label: "External sharing not yet authorized", tone: "lock" };
  return { label: "External sharing locked", tone: "lock" };
}

/** Presentation-only mapping. Does not change persisted nextAction. */
export function nextActionPresentation(nextAction: any, subjectName?: any): string {
  const raw = typeof nextAction === "string" ? nextAction.trim() : "";
  if (!raw) return UNKNOWN;
  // Holmes workspace visibility is already ACTIVE; unresolved action is contractor external disclosure.
  if (/Holmes Murphy workspace authorization and Gate 2 disclosure scope/i.test(raw)
    || /Confirm Holmes Murphy workspace authorization/i.test(raw)
    || /Gate 2 disclosure scope/i.test(raw)) {
    return "Obtain contractor authorization for external disclosure and confirm next underwriting step";
  }
  return raw;
}

const MONTH_ABBREV = [
  "Jan.", "Feb.", "Mar.", "Apr.", "May", "Jun.",
  "Jul.", "Aug.", "Sep.", "Oct.", "Nov.", "Dec.",
] as const;

export function humanDate(input: any): string {
  if (input === null || input === undefined || input === "") return UNKNOWN;
  const text = String(input);
  const match = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return text;
  const month = MONTH_ABBREV[Number(match[2]) - 1] || match[2];
  return `${month} ${Number(match[3])}, ${match[1]}`;
}

/** Presentation-only local date/time. Does not alter stored timestamps. */
export function humanDateTime(input: any): string {
  if (input === null || input === undefined || input === "") return UNKNOWN;
  const parsed = new Date(String(input));
  if (Number.isNaN(parsed.getTime())) return humanDate(input);
  const month = MONTH_ABBREV[parsed.getMonth()] || String(parsed.getMonth() + 1);
  let hours = parsed.getHours();
  const minutes = String(parsed.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  if (hours === 0) hours = 12;
  return `${month} ${parsed.getDate()}, ${parsed.getFullYear()}, ${hours}:${minutes} ${ampm}`;
}

/** Partner-facing source attribution. Hides table names / evidence keys. */
export function informationSourceLabel(sourceSystem: any, sourceLabel?: any): string | null {
  if (typeof sourceLabel === "string" && sourceLabel.trim()) return sourceLabel.trim();
  const key = typeof sourceSystem === "string" ? sourceSystem.trim() : "";
  if (!key) return null;
  if (key === "FINANCIAL_SNAPSHOTS" || /^financial_snapshots/i.test(key)) {
    return "ConstructPay financial snapshot";
  }
  if (key === "ORGANIZATIONS" || /^organizations:/i.test(key)) {
    return "ConstructPay organization profile";
  }
  if (key === "LOCAL_FIXTURE_CONTRACTOR_SUPPLIED") {
    return "Contractor-provided information";
  }
  if (/financial.?snapshot/i.test(key)) return "ConstructPay financial snapshot";
  return "ConstructPay source record";
}

export function informationSourceAsOfLabel(asOf: any): string {
  return humanDate(asOf);
}

/** Presentation-only information item keys. Persisted enums unchanged. */
export function informationItemLabel(itemKey: any, description?: any): string {
  const key = typeof itemKey === "string" ? itemKey.trim() : "";
  const desc = typeof description === "string" ? description.trim() : "";
  if (key === "CUSTOM_REQUEST" || key === "OTHER" || key === "OTHER_INFORMATION") {
    return desc || "Other information or document";
  }
  const labels: Record<string, string> = {
    CURRENT_FINANCIAL_SNAPSHOT_AS_OF: "Current financial snapshot date",
    ORGANIZATION_DISPLAY_NAME: "Organization display name",
    SURETY_APPLICATION_CONTACT_EMAIL: "Surety application contact email",
    CUSTOM_REQUEST: "Other information or document",
  };
  if (!key) return desc || UNKNOWN;
  return labels[key] || desc || humanizeInternalIdentifier(key);
}

/** Presentation-only availability states. */
export function informationAvailabilityLabel(availabilityState: any): string {
  const labels: Record<string, string> = {
    AVAILABLE_EXISTING: "Available in ConstructPay",
    MISSING: "Not available in ConstructPay",
  };
  const key = typeof availabilityState === "string" ? availabilityState.trim() : "";
  if (!key) return UNKNOWN;
  return labels[key] || humanizeInternalIdentifier(key);
}

/** Presentation-only information-item response states. */
export function informationResponseStateLabel(responseState: any): string {
  const labels: Record<string, string> = {
    PENDING: "Awaiting response",
    AUTHORIZED_SHARE: "Share authorized",
    PROVIDED: "Provided by contractor",
    UNAVAILABLE: "Unavailable",
    PENDING_CONTRACTOR: "Awaiting contractor",
  };
  const key = typeof responseState === "string" ? responseState.trim() : "";
  if (!key) return UNKNOWN;
  return labels[key] || humanizeInternalIdentifier(key);
}

/** Presentation-only information-request status. */
export function informationRequestStatusLabel(status: any): string {
  const labels: Record<string, string> = {
    OPEN: "Open",
    RESPONSE_PROVIDED: "Response provided",
    RESOLVED: "Resolved",
  };
  const key = typeof status === "string" ? status.trim() : "";
  if (!key) return UNKNOWN;
  return labels[key] || humanizeInternalIdentifier(key);
}

/**
 * Customer-facing submit confirmation.
 * Separates request submission / partner access from actual external delivery.
 * Does not expose sink/outbox terminology.
 */
export function bondRequestSubmittedAccessCopy(options: {
  partnerDisplayName?: any;
  productionDeliveryEnabled?: boolean;
  deliveryState?: any;
} = {}): string {
  const partner = customerFacingPartnerDisplayName(options.partnerDisplayName);
  const access = `${partner} can now access the authorized request.`;
  const externalSucceeded = options.productionDeliveryEnabled === true
    && String(options.deliveryState || "").toUpperCase() === "SENT";
  if (externalSucceeded) {
    return `${access} ${partner} was notified externally.`;
  }
  return access;
}

export function readinessBandLabel(band: any): string {
  if (!band) return UNKNOWN;
  if (String(band).toUpperCase() === "WATCH") return "Watch";
  return String(band).replace(/_/g, " ");
}

export function readinessFromSnapshot(context: any): {
  constructScore: string | null;
  band: string | null;
  bankability: string | null;
  financialSnapshotDate: string | null;
  arAsOf: string | null;
  apAsOf: string | null;
  wipAsOf: string | null;
} {
  const text = typeof context === "string" ? context : "";
  const scoreMatch = text.match(/\bscore=(\d+)\b/i);
  const bandMatch = text.match(/\bband=([A-Z_]+)\b/i);
  const bankabilityMatch = text.match(/\bbankability_factor=([a-zA-Z_]+)\b/i);
  const snapshotMatch = text.match(/\bsnapshot_date=(\d{4}-\d{2}-\d{2})\b/i);
  const arMatch = text.match(/\bar_aging_as_of=(\d{4}-\d{2}-\d{2})\b/i);
  const apMatch = text.match(/\bap_aging_as_of=(\d{4}-\d{2}-\d{2})\b/i);
  const wipMatch = text.match(/\bwip_source_as_of=(\d{4}-\d{2}-\d{2})\b/i);
  return {
    constructScore: scoreMatch ? scoreMatch[1] : null,
    band: bandMatch ? bandMatch[1] : null,
    bankability: bankabilityMatch ? bankabilityMatch[1] : null,
    financialSnapshotDate: snapshotMatch ? snapshotMatch[1] : null,
    arAsOf: arMatch ? arMatch[1] : null,
    apAsOf: apMatch ? apMatch[1] : null,
    wipAsOf: wipMatch ? wipMatch[1] : null,
  };
}


export function structuredFinancialAsOf(opportunity: {
  structuredFinancialAsOf?: any;
  sourceSnapshotAsOf?: any;
  sourceSnapshotContext?: any;
}): string | null {
  if (opportunity?.structuredFinancialAsOf !== null
      && opportunity?.structuredFinancialAsOf !== undefined
      && String(opportunity.structuredFinancialAsOf) !== "") {
    return String(opportunity.structuredFinancialAsOf);
  }
  const fromContext = readinessFromSnapshot(opportunity?.sourceSnapshotContext).financialSnapshotDate;
  if (fromContext) return fromContext;
  if (opportunity?.sourceSnapshotAsOf) return String(opportunity.sourceSnapshotAsOf);
  return null;
}
export function requestCountLabel(count: any): string {
  if (count === null || count === undefined || count === "") return UNKNOWN;
  const n = Number(count);
  if (!Number.isFinite(n) || n <= 0) return "Not yet created";
  return String(n);
}

export function formatUsd(amount: any): string {
  if (amount === null || amount === undefined || amount === "") return UNKNOWN;
  const n = typeof amount === "number" ? amount : Number(amount);
  if (!Number.isFinite(n)) return String(amount);
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n);
}

function formatCalendarDate(iso: any): string | null {
  const value = typeof iso === "string" ? iso.trim() : "";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [y, m, d] = value.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric", timeZone: "UTC",
  });
}

export const PROJECT_CONTEXT_SOURCE_LABELS: Record<string, string> = {
  SCENARIO_MODELING_CUSTOMER_ENTERED: "Contractor-entered in Scenario Modeling (not imported accounting data)",
};

/**
 * Project context persisted on the canonical Bond request, identical for contractor and partner.
 * Returns no rows for requests created without project context; missing values are omitted.
 */
export function bondRequestProjectContextRows(context: any): Array<{ key: string; label: string; value: string }> {
  if (!context || typeof context.projectName !== "string" || !context.projectName.trim()) return [];
  const rows: Array<{ key: string; label: string; value: string }> = [
    { key: "project", label: "Project", value: context.projectName.trim() },
  ];
  if (context.projectType) rows.push({ key: "type", label: "Project type", value: String(context.projectType) });
  if (context.contractValue !== null && context.contractValue !== undefined) {
    rows.push({ key: "contract", label: "Contract value", value: formatUsd(context.contractValue) });
  }
  rows.push({
    key: "bond",
    label: "Requested bond amount",
    value: context.requestedBondAmount === null || context.requestedBondAmount === undefined
      ? "Not provided"
      : formatUsd(context.requestedBondAmount),
  });
  const start = formatCalendarDate(context.anticipatedStartDate);
  if (start) rows.push({ key: "start", label: "Anticipated start", value: start });
  const end = formatCalendarDate(context.anticipatedEndDate);
  if (end) rows.push({ key: "end", label: "Anticipated completion", value: end });
  rows.push({
    key: "source",
    label: "Source",
    value: PROJECT_CONTEXT_SOURCE_LABELS[String(context.source)] || "Contractor-provided",
  });
  return rows;
}

/**
 * Prefer known request details over empty-state amount language.
 * Does not fabricate amounts; missing amount is shown only when no details exist.
 */
export function requestPrimaryDetailPresentation(request: {
  amount?: any;
  details?: any;
} | null | undefined): { primary: string | null; amountLabel: string | null; details: string | null } {
  if (!request) return { primary: null, amountLabel: null, details: null };
  const detailsRaw = typeof request.details === "string" ? request.details.trim() : "";
  const details = detailsRaw || null;
  const hasAmount = !(request.amount === null || request.amount === undefined || request.amount === "");
  const amountLabel = hasAmount ? formatUsd(request.amount) : null;
  if (details && !hasAmount) {
    return { primary: details, amountLabel: null, details };
  }
  if (hasAmount) {
    return { primary: amountLabel, amountLabel, details };
  }
  return { primary: null, amountLabel: null, details: null };
}

/**
 * Prefer the active canonical Bond/Funding request owner/next-action when present
 * so opportunity-level summary matches request-level workflow state.
 */
export function canonicalOwnerNextAction(opportunity: {
  currentOwner?: any;
  nextAction?: any;
  nextActionDueAt?: any;
  requests?: Array<{
    requestType?: any;
    currentOwner?: any;
    nextAction?: any;
    nextActionDueAt?: any;
  }> | null;
}): { currentOwner: any; nextAction: any; nextActionDueAt: any } {
  const requests = Array.isArray(opportunity?.requests) ? opportunity.requests : [];
  const bond = requests.find((item) => item.requestType === "BOND");
  const funding = requests.find((item) => item.requestType === "FUNDING");
  const primary = bond || funding || null;
  if (primary && (primary.currentOwner || primary.nextAction)) {
    return {
      currentOwner: primary.currentOwner ?? opportunity.currentOwner,
      nextAction: primary.nextAction ?? opportunity.nextAction,
      nextActionDueAt: primary.nextActionDueAt ?? opportunity.nextActionDueAt,
    };
  }
  return {
    currentOwner: opportunity?.currentOwner,
    nextAction: opportunity?.nextAction,
    nextActionDueAt: opportunity?.nextActionDueAt,
  };
}

export function requestStatusLabel(status: any): string {
  if (status === "OPEN") return "Needs review";
  if (status === "CLOSED") return "Closed";
  if (status === "DECLINED") return "Declined";
  if (!status) return UNKNOWN;
  return String(status).replace(/_/g, " ");
}

export function requestStageLabel(stage: any): string {
  if (stage === "INTAKE") return "Intake";
  if (stage === "DOCUMENT_COLLECTION") return "Document collection";
  if (stage === "SUBMITTED") return "Submitted";
  if (stage === "INFORMATION_NEEDED") return "Information needed";
  if (stage === "RESPONSE_PROVIDED") return "Response sent";
  if (!stage) return UNKNOWN;
  return humanizeInternalIdentifier(stage);
}

/** Presentation-only owner alias. Does not change persisted currentOwner. */
export function ownerPresentation(owner: any): string {
  const raw = typeof owner === "string" ? owner.trim() : "";
  if (!raw) return UNKNOWN;
  if (/^ConstructPay BondReady$/i.test(raw)) return "ConstructPay";
  return customerFacingPartnerDisplayName(raw);
}

/**
 * Strip local/fixture prefixes (e.g. "E2E UAT ") from customer-facing partner names.
 * Does not hardcode a partner — uses the configured display name after cleanup.
 */
export function customerFacingPartnerDisplayName(raw: any): string {
  const text = typeof raw === "string" ? raw.trim() : "";
  if (!text) return "Configured partner";
  return text
    .replace(/^E2E\s+UAT\s+/i, "")
    .replace(/^LOCAL\s+(FIXTURE|DEMO|E2E)\s+/i, "")
    .trim() || "Configured partner";
}

/**
 * Map canonical request stage onto the existing pizza-tracker workflow statuses.
 * Presentation-only — does not invent a second lifecycle model.
 */
export function trackerStatusFromCanonicalOpportunity(opportunity: {
  workflowStatus?: any;
  requests?: Array<{ requestType?: any; stage?: any }> | null;
}): any {
  const requests = Array.isArray(opportunity?.requests) ? opportunity.requests : [];
  const bond = requests.find((item) => item.requestType === "BOND");
  const funding = requests.find((item) => item.requestType === "FUNDING");
  const primary = bond || funding || null;
  const stage = primary?.stage;
  if (stage === "INFORMATION_NEEDED") return "DOCS_REQUESTED";
  if (stage === "RESPONSE_PROVIDED") return "PARTNER_REVIEWING";
  if (stage === "SUBMITTED" && opportunity?.workflowStatus === "NOT_REVIEWED") {
    return "NOT_REVIEWED";
  }
  return opportunity?.workflowStatus;
}

/** Customer-facing current-stage label preferring active request stage. */
export function customerFacingCurrentStageLabel(opportunity: {
  workflowStatus?: any;
  requests?: Array<{ requestType?: any; stage?: any }> | null;
}): string {
  const requests = Array.isArray(opportunity?.requests) ? opportunity.requests : [];
  const bond = requests.find((item) => item.requestType === "BOND");
  const funding = requests.find((item) => item.requestType === "FUNDING");
  const primary = bond || funding || null;
  if (primary?.stage) {
    const labeled = requestStageLabel(primary.stage);
    if (labeled && labeled !== UNKNOWN) return labeled;
  }
  return opportunityTrackerView(trackerStatusFromCanonicalOpportunity(opportunity)).currentLabel;
}

/**
 * Humanize SCREAMING_SNAKE tokens embedded in free-text fields such as
 * outstanding_information summaries.
 */
export function humanizeEmbeddedInternalIdentifiers(text: any): string {
  if (text == null || text === "") return text;
  return String(text).replace(/\b[A-Z][A-Z0-9]*(?:_[A-Z0-9]+)+\b/g, (token) => {
    if (token === "CURRENT_FINANCIAL_SNAPSHOT_AS_OF") return "Current financial snapshot date";
    if (token === "ORGANIZATION_DISPLAY_NAME") return "Organization display name";
    if (token === "SURETY_APPLICATION_CONTACT_EMAIL") return "Surety application contact email";
    if (token === "AVAILABLE_EXISTING") return "Available in ConstructPay";
    if (token === "AUTHORIZED_SHARE") return "Share authorized";
    if (token === "MISSING") return "Not available in ConstructPay";
    return humanizeInternalIdentifier(token);
  });
}

/** Direct-customer stage tracker: omit GC-sponsored Gate 1 lifecycle steps. */
export const DIRECT_CUSTOMER_STAGES: ReadonlyArray<readonly [string, string]> = [
  ["NOT_REVIEWED", "Needs review"],
  ["CONSTRUCTPAY_OUTREACH_STARTED", "ConstructPay coordination"],
  ["PARTNER_REVIEWING", "Partner review"],
  ["DOCS_REQUESTED", "Documents requested"],
  ["TERMS_BOND_PATH_RECEIVED", "Terms or bond path received"],
  ["CLOSED", "Closed"],
];

export function stagesForOrigin(originType: any, workflowStatus?: any): ReadonlyArray<readonly [string, string]> {
  if (originType === "DIRECT_CUSTOMER") {
    const stages = [...DIRECT_CUSTOMER_STAGES];
    if (workflowStatus && !stages.some(([status]) => status === workflowStatus) && workflowStatus !== "DECLINED") {
      const fallback = CANONICAL_STAGES.find(([status]) => status === workflowStatus);
      if (fallback) stages.splice(1, 0, fallback);
    }
    return stages;
  }
  return CANONICAL_STAGES;
}

/**
 * Pizza-tracker presentation stages mapped from persisted workflow statuses.
 * Presentation-only — does not create a second lifecycle model.
 * Stage order follows the server-authoritative forward transition path.
 */
export const OPPORTUNITY_TRACKER_STAGES: ReadonlyArray<{
  key: string;
  label: string;
  statuses: readonly string[];
}> = [
  { key: "submitted", label: "Submitted", statuses: ["NOT_REVIEWED"] },
  {
    key: "constructpay_review",
    label: "ConstructPay Review",
    statuses: ["GC_APPROVED_FOR_CONSTRUCTPAY_OUTREACH", "CONSTRUCTPAY_OUTREACH_STARTED"],
  },
  {
    key: "partner_review",
    label: "Partner Review",
    statuses: [
      "TRADE_PARTNER_REVIEWING",
      "TRADE_PARTNER_APPROVED_INTRODUCTION",
      "INTRODUCED_TO_EXTERNAL_PARTNER",
    ],
  },
  { key: "underwriting", label: "Underwriting", statuses: ["PARTNER_REVIEWING"] },
  { key: "additional_info", label: "Additional Information", statuses: ["DOCS_REQUESTED"] },
  { key: "terms", label: "Terms / Bond Path", statuses: ["TERMS_BOND_PATH_RECEIVED"] },
  { key: "complete", label: "Complete", statuses: ["CLOSED"] },
];

export type TrackerStageState = "completed" | "current" | "pending";

export type OpportunityTrackerView = {
  stages: ReadonlyArray<{
    key: string;
    label: string;
    index: number;
    state: TrackerStageState;
  }>;
  currentIndex: number;
  currentLabel: string;
  declined: boolean;
};

/** Map persisted workflowStatus onto pizza-tracker completed/current/pending stages. */
export function opportunityTrackerView(workflowStatus: any): OpportunityTrackerView {
  const declined = workflowStatus === "DECLINED";
  let currentIndex = OPPORTUNITY_TRACKER_STAGES.findIndex((stage) =>
    stage.statuses.includes(workflowStatus)
  );
  // Fail conservative: unknown status shows first stage as current without inventing progress.
  if (currentIndex < 0) currentIndex = declined ? -1 : 0;

  const stages = OPPORTUNITY_TRACKER_STAGES.map((stage, index) => {
    let state: TrackerStageState = "pending";
    if (!declined && currentIndex >= 0) {
      if (index < currentIndex) state = "completed";
      else if (index === currentIndex) state = "current";
    }
    return { key: stage.key, label: stage.label, index, state };
  });

  return {
    stages,
    currentIndex,
    currentLabel:
      declined
        ? "Declined"
        : currentIndex >= 0
          ? OPPORTUNITY_TRACKER_STAGES[currentIndex].label
          : workflowLabel(workflowStatus),
    declined,
  };
}

/** Broker-facing activity copy. Raw note/eventType remain in audit. */
export function activityPresentation(event: ApiRecord): { headline: string; detail?: string | null } {
  const type = event?.eventType;
  const note = typeof event?.note === "string" ? event.note : "";
  if (type === "OPPORTUNITY_CREATED") {
    return { headline: "Opportunity added to BondReady" };
  }
  if (type === "BOND_REQUEST_CREATED") {
    const amount = note.match(/\$[\d,]+/)?.[0];
    if (amount && /Ironworkers Local 512 Wage & Welfare bond/i.test(note)) {
      return { headline: `Bond request added — ${amount} Ironworkers Local 512 Wage & Welfare bond` };
    }
    return { headline: amount ? `Bond request added — ${amount}` : "Bond request added" };
  }
  if (type === "FUNDING_REQUEST_CREATED") {
    const amount = note.match(/\$[\d,]+/)?.[0];
    if (amount && /working-capital|revolving line/i.test(note)) {
      return { headline: `Working-capital request added — ${amount} revolving line` };
    }
    return { headline: amount ? `Working-capital request added — ${amount}` : "Working-capital request added" };
  }
  if (type === "GATE_1_DECIDED") {
    return { headline: "GC outreach authorization recorded" };
  }
  if (type === "GATE_2_STATE_CHANGED") {
    return { headline: "External sharing authorization updated" };
  }
  if (type === "OPPORTUNITY_STATUS_CHANGED") {
    const from = event?.priorStatus ? workflowLabel(event.priorStatus) : null;
    const to = event?.newStatus ? workflowLabel(event.newStatus) : null;
    if (from && to && from !== UNKNOWN && to !== UNKNOWN) {
      return { headline: "Opportunity status updated", detail: `${from} → ${to}` };
    }
    return { headline: "Opportunity status updated" };
  }
  if (type === "PARTNER_VISIBILITY_GRANTED") {
    return { headline: "Partner review access established" };
  }
  if (type === "PARTNER_VISIBILITY_REVOKED") {
    return { headline: "Partner review access revoked" };
  }
  if (type === "BOND_REQUEST_UPDATED") {
    return { headline: "Bond request updated" };
  }
  if (type === "FUNDING_REQUEST_UPDATED") {
    return { headline: "Working-capital request updated" };
  }
  if (type === "BOND_REQUEST_SUBMITTED") {
    return { headline: "Bond request submitted" };
  }
  if (type === "INFORMATION_REQUESTED") {
    return { headline: "Additional information requested" };
  }
  if (type === "INFORMATION_RESPONSE_PROVIDED") {
    return { headline: "Information response provided" };
  }
  if (type === "INFORMATION_SHARED") {
    return { headline: "Authorized information shared" };
  }
  if (type === "INFORMATION_REQUEST_RESOLVED") {
    return { headline: "Information request resolved" };
  }
  if (type === "INFORMATION_ATTACHMENT_WITHDRAWN") {
    return {
      headline: "Shared document withdrawn",
      detail: event?.resultingState === "PENDING" ? "Requested information is needed again" : null,
    };
  }
  // Fail-safe: never dump SCREAMING_SNAKE identifiers into the customer timeline.
  return { headline: humanizeInternalIdentifier(type) };
}

/**
 * Infrastructure notification/outbox events stay in audit history but are not
 * customer-facing BondReady lifecycle milestones.
 */
const CUSTOMER_TIMELINE_HIDDEN_EVENT_TYPES = new Set([
  // Not a meaningful customer/partner milestone (common for DIRECT_CUSTOMER).
  "GATE_1_NOT_APPLICABLE",
  "BOND_REQUEST_NOTIFICATION_ENQUEUED",
  "BOND_REQUEST_NOTIFICATION_SENT",
  "BOND_REQUEST_NOTIFICATION_SUPPRESSED",
  "BOND_REQUEST_NOTIFICATION_FAILED",
  "BOND_REQUEST_INFORMATION_REQUEST_NOTIFICATION_ENQUEUED",
  "BOND_REQUEST_INFORMATION_REQUEST_NOTIFICATION_SENT",
  "BOND_REQUEST_INFORMATION_REQUEST_NOTIFICATION_SUPPRESSED",
  "BOND_REQUEST_INFORMATION_REQUEST_NOTIFICATION_FAILED",
]);

export function isCustomerFacingTimelineEvent(event: ApiRecord): boolean {
  const type = event?.eventType;
  if (!type) return false;
  return !CUSTOMER_TIMELINE_HIDDEN_EVENT_TYPES.has(String(type));
}

function humanizeInternalIdentifier(input: any): string {
  const raw = typeof input === "string" ? input.trim() : "";
  if (!raw) return UNKNOWN;
  if (workflowLabel(raw) !== UNKNOWN) return workflowLabel(raw);
  return raw
    .toLowerCase()
    .split(/_+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

/**
 * Presentation for "Structured financial data through".
 * Does not invent dates. When ConstructPay has CURRENT quality but this
 * opportunity record has no attached as-of, explain that clearly instead of
 * implying the source data is missing from ConstructPay entirely.
 */
export function structuredFinancialThroughLabel(
  asOf: any,
  dataQualityStatus?: any,
  options?: { viewerRole?: string; ownerResolvedAsOf?: any }
): string {
  const resolved = asOf || options?.ownerResolvedAsOf;
  if (resolved !== null && resolved !== undefined && resolved !== "") {
    return humanDate(resolved);
  }
  if (dataQualityStatus === "CURRENT") {
    if (options?.viewerRole === "AUTHORIZED_PARTNER") {
      return "Source-backed snapshot on file (as-of date not included in this authorized view)";
    }
    return "Source-backed snapshot on file (as-of date not attached to this opportunity yet)";
  }
  if (dataQualityStatus === "STALE") {
    return "Source-backed snapshot on file is stale (as-of date not attached to this opportunity)";
  }
  return UNKNOWN;
}

export function dueLabel(input: any): string {
  if (input === null || input === undefined || input === "") return "Not set";
  return humanDate(input);
}

export function isActiveOpportunity(opportunity: ApiRecord): boolean {
  return !["CLOSED", "DECLINED"].includes(opportunity.workflowStatus);
}

export function needsAction(opportunity: ApiRecord): boolean {
  if (!isActiveOpportunity(opportunity)) return false;
  return [
    "NOT_REVIEWED",
    "GC_APPROVED_FOR_CONSTRUCTPAY_OUTREACH",
    "CONSTRUCTPAY_OUTREACH_STARTED",
    "TRADE_PARTNER_REVIEWING",
    "PARTNER_REVIEWING",
    "DOCS_REQUESTED",
  ].includes(opportunity.workflowStatus) || !opportunity.workflowStatus;
}

export function workspaceReviewLabel(viewerRole: any): { label: string; tone: "green" | "amber" | "lock" } {
  if (viewerRole === "OWNER") return { label: "Owner workspace", tone: "green" };
  if (viewerRole === "AUTHORIZED_PARTNER") return { label: "Workspace review authorized", tone: "green" };
  if (viewerRole === "CONSTRUCTPAY_OPERATOR") {
    return { label: "Operator workflow access", tone: "amber" };
  }
  return { label: "Workspace review locked", tone: "lock" };
}

export function requestTypeSummary(opportunity: ApiRecord): string {
  const count = Number(opportunity.requestCount) || 0;
  if (count <= 0) return "None";
  const parts: string[] = [];
  if (opportunity.bondAmount !== null && opportunity.bondAmount !== undefined) parts.push("Bond");
  if (opportunity.fundingAmount !== null && opportunity.fundingAmount !== undefined) parts.push("Working capital");
  if (parts.length) return `${count} · ${parts.join(" + ")}`;
  return requestCountLabel(opportunity.requestCount);
}

export function hasRequestsInProgress(opportunity: ApiRecord): boolean {
  return Number(opportunity.requestCount) > 0;
}

export function awaitingPartner(opportunity: ApiRecord): boolean {
  return [
    "TRADE_PARTNER_REVIEWING",
    "TRADE_PARTNER_APPROVED_INTRODUCTION",
    "PARTNER_REVIEWING",
    "DOCS_REQUESTED",
  ].includes(opportunity.workflowStatus);
}

export function deriveOperationalKpis(opportunities: ApiRecord[]): {
  activeOpportunities: number;
  needsAction: number;
  requestsInProgress: number;
  awaitingPartner: number;
} {
  return {
    activeOpportunities: opportunities.filter(isActiveOpportunity).length,
    needsAction: opportunities.filter(needsAction).length,
    requestsInProgress: opportunities.filter(hasRequestsInProgress).length,
    awaitingPartner: opportunities.filter(awaitingPartner).length,
  };
}

export function Field({ label, children }: { label: string; children?: React.ReactNode }) {
  return (
    <div>
      <div style={{
        color: "#9fb0c4",
        fontSize: 11,
        fontWeight: 800,
        textTransform: "uppercase" as const,
        letterSpacing: "0.06em",
        marginBottom: 4,
      }}
      >
        {label}
      </div>
      <div style={{ color: "#edf5ff", fontSize: 13, overflowWrap: "anywhere" as const }}>{children}</div>
    </div>
  );
}
